import { Group } from "@dinahmoe/dmafjs-visual";
import { coreEngine, nodes, store } from "@dinahmoe/dmafjs-core";


class SearchContainer extends Group {
  namespace = "se26:searchContainer";


  handlers = {
    scrollValue: this.handleScrollValue
  };

  init(node, event) {
    super.init(node, event);
    node._selectedCategory = null;
    node._selectedCategoryIndex = null;
    node._resultNodes = [];
    node._selectedResultItem = null;
    node._absoluteY = 0;

    const casesData = store.get("assets.data.cases_export");
    if (casesData) {
      node._categories = casesData.categories;
      node._casesData = casesData;

      if (node._categories.length > 0) {
        node._selectedCategory = node._categories[0].id;
        node._selectedCategoryIndex = 0;
        this.updatePillState(node, 0, true);
        this.showResults(node, node._categories[0].id);
      }
    }
  }

  state(node, config, event) {
    const parts = event.name.split(":");
    const action = parts[parts.length - 1];

    if (action === "select_category") {
      this.handleCategoryClick(node, event);
    } else if (action === "open_case") {
      this.handleOpenCase(node, event);
    } else if (action === "closed") {
      if (node._selectedResultItem) {
        this.updateResultItemState(node._selectedResultItem, false);
        node._selectedResultItem = null;
      }
      node._isClosing = false;
      node._openScrollValue = null;
    } else if (action === "case_changed") {
      const categoryId = event.data?.categoryId;
      const caseId = event.data?.caseId;

      if (categoryId && categoryId !== node._selectedCategory) {
        const index = node._categories.findIndex(c => c.id === categoryId);
        if (index !== -1) {
          if (node._selectedCategoryIndex !== null) {
            this.updatePillState(node, node._selectedCategoryIndex, false);
          }
          node._selectedCategory = categoryId;
          node._selectedCategoryIndex = index;
          this.updatePillState(node, index, true);
          this.showResults(node, categoryId);
        }
      }

      // Deselect old result item
      if (node._selectedResultItem) {
        this.updateResultItemState(node._selectedResultItem, false);
        node._selectedResultItem = null;
      }

      // Select new result item if it exists in current results
      if (caseId) {
        const matchingResult = node._resultNodes.find(r => r.caseStory.id === caseId);
        if (matchingResult) {
          this.updateResultItemState(matchingResult.node, true);
          node._selectedResultItem = matchingResult.node;
        }
      }
    }
  }

  // --- Category tabs ---

  handleCategoryClick(node, event) {
    const id = event.data?.id;
    if (!id) return;

    const index = parseInt(id.replace("cat_", ""));
    if (isNaN(index) || !node._categories) return;

    const category = node._categories[index];
    if (!category) return;

    if (node._selectedCategory === category.id) {
      this.deselectCategory(node);
      return;
    }

    if (node._selectedCategory !== null) {
      this.updatePillState(node, node._selectedCategoryIndex, false);
    }

    node._selectedCategory = category.id;
    node._selectedCategoryIndex = index;
    this.updatePillState(node, index, true);
    this.showResults(node, category.id);
    this.scrollToCenter(node);
  }

  deselectCategory(node) {
    this.updatePillState(node, node._selectedCategoryIndex, false);
    node._selectedCategory = null;
    node._selectedCategoryIndex = null;
    this.clearResults(node);
  }

  updateResultItemState(resultNode, selected) {
    const els = resultNode._ids;
    if (selected) {
      resultNode["appearance.background"] = "#FFFFFF";
      resultNode["appearance.backdropBlur"] = 0;
      resultNode["border.width"] = 0;
      if (els?.headline) els.headline["text.color"] = "#262223";
      if (els?.icon) {
        els.icon["source.imageId"] = "icon_minus";
        els.icon["source.fill"] = "#262223";
      }
    } else {
      resultNode["appearance.background"] = "rgba(255,255,255, 0.1)";
      resultNode["appearance.backdropBlur"] = 10;
      resultNode["border.width"] = 1;
      if (els?.headline) els.headline["text.color"] = "#FFFFFF";
      if (els?.icon) {
        els.icon["source.imageId"] = "icon_plus_white";
        els.icon["source.fill"] = "white";
      }
    }
  }

  updatePillState(node, index, selected) {
    const categoriesGroup = node._ids?.search_categories;
    if (!categoriesGroup) return;

    const pill = categoriesGroup._ids?.[`cat_${index}`];
    if (!pill) return;

    const textNode = pill._ids?.text;

    if (selected) {
      pill["appearance.background"] = "#FFFFFF";
      if (textNode) textNode["text.color"] = "#262223";
    } else {
      pill["appearance.background"] = "transparent";
      if (textNode) textNode["text.color"] = "#FFFFFF";
    }
  }

  showResults(node, categoryId) {
    this.clearResults(node);

    const caseStories = node._casesData.case_stories.filter(
      c => c.category_id === categoryId
    );

    const template = store.get("template.visual.search_result_item");
    if (!template) return;

    const resultsList = node._ids?.results_list;
    if (!resultsList) return;

    caseStories.forEach((caseStory, index) => {
      const itemNode = coreEngine.create({
        ...template,
        parentUid: resultsList.uid,
        id: `result_${index}`,
        indexIds: true
      }, { key: "create_search_result" });

      const els = itemNode._ids;
      if (els?.headline) {
        els.headline["text.content"] = caseStory.headline || "";
      }

      node._resultNodes.push({ node: itemNode, caseStory });
    });
  }

  clearResults(node) {
    node._selectedResultItem = null;
    if (node._resultNodes) {
      node._resultNodes.forEach(item => coreEngine.destroy(item.node.uid));
      node._resultNodes = [];
    }
  }

  // --- Case story ---

  handleOpenCase(node, event) {
    const id = event.data?.id;
    if (!id) return;

    const index = parseInt(id.replace("result_", ""));
    if (isNaN(index)) return;

    const resultInfo = node._resultNodes[index];
    if (!resultInfo) return;

    const caseData = resultInfo.caseStory;

    // Toggle: clicking the same item closes the overlay
    if (node._selectedResultItem === resultInfo.node) {
      this.updateResultItemState(resultInfo.node, false);
      node._selectedResultItem = null;
      node._openScrollValue = null;
      nodes.get("container_case_story")?.tell({ type: "close" });
      return;
    }

    // Handle selection locally
    if (node._selectedResultItem) {
      this.updateResultItemState(node._selectedResultItem, false);
    }
    this.updateResultItemState(resultInfo.node, true);
    node._selectedResultItem = resultInfo.node;

    // Store scroll position for close behavior — defer scroll-close check
    // to let layout settle after content container resizes
    node._openScrollValue = node.scrollValue;
    node._isClosing = false;
    node._isSettling = true;
    setTimeout(() => { node._isSettling = false; }, 800);

    // Tell overlay manager to open this case
    nodes.get("container_case_story")?.tell({
      type: "open_case",
      caseId: caseData.id,
      sourceUid: node.uid
    });

    // Scroll to section if far away
    if (node._absoluteY > 0 && Math.abs(node.scrollValue - node._absoluteY) > 200) {
      this.scrollToCenter(node);
    }
  }

  receiveMessage(node, message) {
    if (message.key === "absolute_position") {
      node._absoluteY = message.value;
    } else {
      super.receiveMessage(node, message);
    }
  }

  scrollToCenter(node) {
    if (node.smootherUid) {
      const smoother = nodes.get(node.smootherUid);
      if (smoother && node._absoluteY) {
        smoother["value.target"] = node._absoluteY;
      }
    }
  }

  handleScrollValue(node) {
    if (node._isClosing || node._isSettling || node._openScrollValue == null || !node._absoluteY) return;

    const viewportHeight = nodes.get("root")["size.height"];
    const threshold = viewportHeight / 2;
    const diff = Math.abs(node.scrollValue - node._openScrollValue);

    if (diff > threshold) {
      node._isClosing = true;
      nodes.get("container_case_story")?.tell({ type: "close" });
    }
  }
}

const searchContainer = new SearchContainer();
export { searchContainer, SearchContainer };
